/**
 * Impressão de imagens de solicitação — fonte ÚNICA do cabeçalho.
 *
 * Toda impressão de imagem do módulo passa por aqui (visualizador, botão
 * "Imprimir imagens" do detalhe e, adiante, a folha do lote), para o cabeçalho
 * sair sempre igual: descrição do item · nº da solicitação · lote.
 *
 * Assar o cabeçalho em HTML (e não dentro do canvas da imagem) é de propósito:
 * o texto sai nítido na impressora — desenhado no canvas ele herdaria a
 * resolução da foto, que é comprimida a 1280px.
 *
 * RQA/RQI não têm item associado à foto (a FK é para a solicitação, não para o
 * item), então nesses tipos o cabeçalho degrada para cliente + pedido — ver
 * `cabecalhoDaFoto`.
 */

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Monta o cabeçalho de UMA foto a partir da solicitação e, quando existir, da
 * peça dona da foto.
 *
 * @param {object} solicitacao detalhe carregado da API
 * @param {object|null} peca   peça dona da foto (só em ASM/AST/ASP)
 */
export function cabecalhoDaFoto(solicitacao, peca) {
  const linhas = [];
  if (peca) {
    // Assistência: a foto é de uma peça — é o dado mais útil no papel.
    const descricao = [peca.descricao, peca.ambiente].filter(Boolean).join(" · ");
    if (descricao) linhas.push(descricao);
  } else {
    // Requisição: não há item associado. Degrada para o que identifica.
    const alternativa = [
      solicitacao?.cliente_nome,
      solicitacao?.num_pedido ? `Pedido ${solicitacao.num_pedido}` : "",
    ].filter(Boolean).join(" · ");
    if (alternativa) linhas.push(alternativa);
  }

  const lote = peca?.lote_numero
    || (solicitacao?.lotes_numeros || [])[0]
    || "";

  return {
    titulo: solicitacao?.numero || "",
    detalhe: linhas.join(" · "),
    lote: lote ? `LOTE ${lote}` : "",
  };
}

/**
 * Junta TODAS as imagens de uma solicitação já com o cabeçalho de cada uma.
 *
 * Fonte única para o botão "Imprimir imagens" do detalhe e para a folha do
 * lote — o cabeçalho sai igual nos dois porque nasce no mesmo lugar.
 *
 * @param {object} solicitacao detalhe carregado da API
 * @returns {Array<{id, imagem, cabecalho, peca}>}
 */
export function paginasDaSolicitacao(solicitacao) {
  if (!solicitacao) return [];
  const paginas = [];

  // Assistência (ASM/AST/ASP): a foto pertence a uma peça.
  (solicitacao.pecas || []).forEach((peca) => {
    (peca.fotos || []).forEach((foto) => {
      if (!foto?.imagem) return;
      paginas.push({
        id: foto.id,
        imagem: foto.imagem,
        peca,
        cabecalho: cabecalhoDaFoto(solicitacao, peca),
      });
    });
  });

  // Requisição (RQA/RQI): a foto pende direto da solicitação.
  (solicitacao.fotos || []).forEach((foto) => {
    if (!foto?.imagem) return;
    paginas.push({
      id: foto.id,
      imagem: foto.imagem,
      peca: null,
      cabecalho: cabecalhoDaFoto(solicitacao, null),
    });
  });

  return paginas;
}

const CSS_FOLHA = `
  * { box-sizing: border-box; margin: 0; }
  @page { size: A4 portrait; margin: 10mm; }
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #16181e; }
  .pg { page-break-after: always; display: flex; flex-direction: column; height: 277mm; }
  /* last-of-type e nao last-child: o <script> e o ultimo FILHO do body, entao
     last-child nunca casaria e sairia uma folha em branco no fim. */
  .pg:last-of-type { page-break-after: auto; }
  .cab {
    flex: none; border-bottom: 1px solid #c9ccd6; padding-bottom: 6px;
    margin-bottom: 8px; display: flex; align-items: baseline; gap: 10px;
  }
  .cab .num { font-size: 15px; font-weight: 800; white-space: nowrap; }
  .cab .det { font-size: 12px; color: #4b5060; flex: 1; overflow-wrap: anywhere; }
  .cab .lote {
    font-size: 12px; font-weight: 700; white-space: nowrap;
    border: 1px solid #c9ccd6; border-radius: 6px; padding: 1px 7px;
  }
  .arte { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; }
  .arte img { max-width: 100%; max-height: 100%; object-fit: contain; }
`;

function folhaHtml({ imagem, cabecalho }) {
  const { titulo, detalhe, lote } = cabecalho || {};
  return `
    <div class="pg">
      <div class="cab">
        <span class="num">${esc(titulo)}</span>
        <span class="det">${esc(detalhe)}</span>
        ${lote ? `<span class="lote">${esc(lote)}</span>` : ""}
      </div>
      <div class="arte"><img src="${esc(imagem)}" alt="${esc(detalhe || titulo)}" /></div>
    </div>`;
}

/**
 * Abre a janela de impressão com uma folha por imagem.
 * @param {Array<{imagem: string, cabecalho: object}>} paginas
 * @returns {boolean} false quando o navegador bloqueou o pop-up
 */
export function imprimirImagens(paginas) {
  const lista = (paginas || []).filter((p) => p && p.imagem);
  if (!lista.length) return true; // nada a imprimir não é erro

  const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>Imagens ${esc(lista[0].cabecalho?.titulo || "")}</title>
<style>${CSS_FOLHA}</style></head>
<body>${lista.map(folhaHtml).join("")}
<script>window.onload=function(){setTimeout(function(){window.print();},120);};</script>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
